<template>
  <v-card color="accent">
    <v-sheet height="64">
      <v-toolbar
        flat
        color="accent"
      >
        <v-btn
          outlined
          class="mr-4"
          :color="btnsColor"
          @click="setToday"
        >
          Hoje
        </v-btn>

        <v-tooltip
          top
          color="primary"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              fab
              text
              small
              :color="btnsColor"
              v-bind="attrs"
              v-on="on"
              @click="prev"
            >
              <v-icon small>
                {{ icons.mdiChevronLeft }}
              </v-icon>
            </v-btn>
          </template>

          <span>Mês anterior</span>
        </v-tooltip>

        <v-tooltip
          top
          color="primary"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              fab
              text
              small
              :color="btnsColor"
              v-bind="attrs"
              v-on="on"
              @click="next"
            >
              <v-icon small>
                {{ icons.mdiChevronRight }}
              </v-icon>
            </v-btn>
          </template>

          <span>Próximo mês</span>
        </v-tooltip>

        <v-toolbar-title v-if="$refs.calendar">
          {{ $refs.calendarRef.title }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          outlined
          class="mr-4"
          :color="btnsColor"
          @click="type = 'day'"
        >
          Dia
        </v-btn>

        <v-btn
          outlined
          class="mr-4"
          :color="btnsColor"
          @click="type = 'week'"
        >
          Semana
        </v-btn>

        <v-btn
          outlined
          :color="btnsColor"
          @click="type = 'month'"
        >
          Mês
        </v-btn>
      </v-toolbar>
    </v-sheet>

    <v-sheet height="600">
      <v-calendar
        ref="calendarRef"
        v-model="focus"
        :attributes="attrs"
        color="primary"
        locale="pt-br"
        :events="eventsLocale"
        :event-color="getEventColor"
        :type="type"
        :start="startDate"
        :end="endDate"
        class="calendar"
        :interval-style="intervalStyle"
        @click:event="handleClickEvent"
        @click:more="viewDay"
        @click:date="viewDay"
      >
        <!-- <template #day-label="{ day }">
          <div
            class="dayLabel"
            @click="viewDay"
          >
            <span>{{ day }}</span>
          </div>
        </template> -->

        <template #event="{ event }">
          <calendario-mensal-evento
            :is-cadastro-agendamento="isCadastroAgendamento"
            :evento="event"
          />
        </template>
      </v-calendar>

      <selecionar-evento-dialog
        :is-dialog-open.sync="isSelecionarEventoDialogOpen"
        :selecionar-evento="selecionarEvento"
      />

      <evento-selecionado-dialog
        v-if="isEventoSelecionadoDialogOpen"
        :is-dialog-open.sync="isEventoSelecionadoDialogOpen"
        :evento-selecionado="eventoSelecionado"
      />
    </v-sheet>
  </v-card>
</template>

<style lang="scss" scoped>
.calendar {
  background-color: rgb(181, 181, 181, 0.4);
}
</style>

<script>
import { onMounted, ref, computed, watch } from '@vue/composition-api'
import { mdiChevronLeft, mdiChevronRight, mdiDotsVertical, mdiHeart, mdiMenuDown, mdiPencil } from '@mdi/js'
import EventoSelecionadoDialog from './EventoSelecionadoDialog.vue'
import SelecionarEventoDialog from './SelecionarEventoDialog.vue'
import CalendarioMensalEvento from './CalendarioMensalEvento.vue'
import { getDataAtualNoFormatoIsoDateString } from '@/utils/dateTime'
import useAppConfig from '@/@core/@app-config/useAppConfig'

export default {
  emits: ['evento-selecionado'],

  components: {
    EventoSelecionadoDialog,
    SelecionarEventoDialog,
    CalendarioMensalEvento,
  },

  props: {
    isCadastroAgendamento: {
      type: Boolean,
      required: true,
    },
    events: {
      type: Array,
      default: null,
    },
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
  },

  setup(props, { emit }) {
    const calendarRef = ref(null)
    const focus = ref(getDataAtualNoFormatoIsoDateString())
    const type = ref('month')
    const typeToLabel = {
      month: 'Mês',
      week: 'Semana',
      day: 'Dia',
    }
    const eventoSelecionado = ref({})
    const isEventoSelecionadoDialogOpen = ref(false)
    const eventsLocale = ref([])
    const isSelecionarEventoDialogOpen = ref(false)

    const { isDark } = useAppConfig()
    const btnsColor = computed(() => (isDark.value ? '' : 'white'))

    onMounted(() => {
      if (!calendarRef.value) return
      calendarRef.value.checkChange()
    })

    function viewDay({ date }) {
      focus.value = date
      type.value = 'day'
    }

    function getEventColor(event) {
      return event.color
    }

    function setToday() {
      focus.value = ''
    }

    function prev() {
      calendarRef.value.prev()
    }

    function next() {
      calendarRef.value.next()
    }

    function selecionarEvento() {
      emit('evento-selecionado', eventoSelecionado.value)
    }

    function openSelecionarEventoDialog() {
      isSelecionarEventoDialogOpen.value = true
    }

    function showEvent(nativeEvent) {
      const open = () => {
        requestAnimationFrame(() => requestAnimationFrame(() => { isEventoSelecionadoDialogOpen.value = true }))
      }

      if (isEventoSelecionadoDialogOpen.value) {
        isEventoSelecionadoDialogOpen.value = false
        requestAnimationFrame(() => requestAnimationFrame(() => open()))
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    }

    function handleClickEvent({ nativeEvent, event }) {
      eventoSelecionado.value = event

      if (props.isCadastroAgendamento) {
        openSelecionarEventoDialog()
      } else {
        showEvent(nativeEvent)
      }
    }

    watch(
      () => props.events,
      v => {
        if (!v) {
          eventsLocale.value = []
          return
        }
        eventsLocale.value = v
      },
      {
        immediate: true,
        deep: true,
      },
    )

    return {
      calendarRef,
      focus,
      type,
      typeToLabel,
      isEventoSelecionadoDialogOpen,
      eventsLocale,
      isSelecionarEventoDialogOpen,

      btnsColor,

      viewDay,
      getEventColor,
      setToday,
      prev,
      next,
      eventoSelecionado,
      selecionarEvento,
      handleClickEvent,

      attrs: {
        highlight: {
          class: 'date-circle',
          contentClass: 'date-text',
        },
      },

      intervalStyle() {
        return {
          color: 'red',
        }
      },

      icons: {
        mdiChevronLeft,
        mdiChevronRight,
        mdiPencil,
        mdiHeart,
        mdiMenuDown,
        mdiDotsVertical,
      },
    }
  },
}
</script>
